import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../../styles/styles";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { server, backend_url } from "../../server";
import { toast } from "react-toastify";
import { clearCart } from "../../redux/actions/cart";
import { loadUser } from "../../redux/actions/user";
import {
    saveStripePendingCheckout,
    clearStripePendingCheckout,
    registerStripePendingCheckout,
    paymentIntentIdFromClientSecret,
} from "../../utils/stripePendingCheckout";
import {
    normalizeProductDisplayName,
    computeBuyerFacingTotalFromCheckoutPayload,
    sumPostageFees,
    bundlePostageDiscountLabel,
} from "../../utils/checkoutPricing";
import useEnrichedCartPostage from "../../hooks/useEnrichedCartPostage";
import { assertCartLinesInStock } from "../../utils/productStock";
import {
    buildStripeDefaultBilling,
    getStripeBillingPrefillKey,
    getStripeElementsOptions,
    getStripePaymentElementOptions,
    STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS,
} from "../../utils/stripePaymentUi";
import StripeExpressCheckout from "./StripeExpressCheckout";

/** Turn Stripe / browser errors into plain language for toast (avoid raw API strings). */
function humanizeStripePaymentError(err) {
    const m = String(err?.message || "");
    const low = m.toLowerCase();
    if (!m) return "Payment could not be completed. Please try again or choose another method.";
    if (low.includes("return_url") && low.includes("klarna")) {
        return "Klarna opens a secure page to finish payment. Please try again; if it keeps failing, use card or wallet.";
    }
    if (low.includes("return_url")) {
        return "This payment method needs a secure redirect. Please try again or use card.";
    }
    return m.replace(/`/g, "'");
}

// Currency formatter for GBP
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(Number(value));
};

const Payment = ({ currency, orderDataOverride = null, stripePromise = null }) => {
    const [orderData] = useState(() => {
        if (orderDataOverride && typeof orderDataOverride === "object") {
            return orderDataOverride;
        }
        try {
            return JSON.parse(localStorage.getItem("latestOrder")) || {};
        } catch {
            return {};
        }
    });
    const [method, setMethod] = useState("");
    const [walletUseAmount, setWalletUseAmount] = useState(0);
    const [walletPaying, setWalletPaying] = useState(false);
    const { user } = useSelector((state) => state.user);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(loadUser());
    }, [dispatch]);

    const { cartWithPostage } = useEnrichedCartPostage(orderData?.cart);

    const orderDataForPricing = useMemo(() => {
        const postageFees = sumPostageFees(cartWithPostage);
        return { ...orderData, cart: cartWithPostage, postageFees };
    }, [orderData, cartWithPostage]);

    const resolvedUkara = (orderData?.ukaraNumber || user?.ukaraNumber || "").toString().trim().toUpperCase();
    const checkoutProfile =
      orderData?.user && typeof orderData.user === "object" ? orderData.user : {};
    const mergedBuyer =
      user || Object.keys(checkoutProfile).length
        ? {
            ...checkoutProfile,
            ...(user || {}),
            email: user?.email || checkoutProfile?.email || "",
            ukaraNumber: resolvedUkara,
          }
        : null;
    const normalizedCart = (orderDataForPricing?.cart || []).map((item) => {
        const fallbackShopId =
            item?.shopId ||
            item?.shop?._id ||
            item?.shop?.id ||
            item?.sellerId ||
            (typeof item?.shop === "string" ? item.shop : "");

        return {
            ...item,
            shopId: fallbackShopId ? String(fallbackShopId) : undefined,
            productId: item?.productId || item?._id || item?.id || undefined,
        };
    });

    const totalAmount = computeBuyerFacingTotalFromCheckoutPayload(orderDataForPricing);
    const order = useMemo(
        () => ({
            cart: normalizedCart,
            shippingAddress: orderData?.shippingAddress,
            user: mergedBuyer,
            totalPrice: String(Number(totalAmount).toFixed(2)),
            subTotalPrice: orderDataForPricing?.subTotalPrice,
            discountPrice: orderDataForPricing?.discountPrice,
            discountCode: orderDataForPricing?.discountCode,
            shipping: orderDataForPricing?.shipping,
            buyerProtectionFee: orderDataForPricing?.buyerProtectionFee,
            postageFees: orderDataForPricing?.postageFees,
            bundleDiscount: orderDataForPricing?.bundleDiscount,
            bundlePostageDiscount: orderDataForPricing?.bundlePostageDiscount,
            freeShipping: orderDataForPricing?.freeShipping,
            ukaraNumber: resolvedUkara,
        }),
        [
            normalizedCart,
            orderData?.shippingAddress,
            mergedBuyer,
            totalAmount,
            orderDataForPricing?.subTotalPrice,
            orderDataForPricing?.discountPrice,
            orderDataForPricing?.discountCode,
            orderDataForPricing?.shipping,
            orderDataForPricing?.buyerProtectionFee,
            orderDataForPricing?.postageFees,
            orderDataForPricing?.bundleDiscount,
            orderDataForPricing?.bundlePostageDiscount,
            orderDataForPricing?.freeShipping,
            resolvedUkara,
        ]
    );
    const walletBalance = Number(user?.availableBalance || 0);
    const stripeAvailable = Boolean(stripePromise);

    useEffect(() => {
        if (method === "wallet") {
            setWalletUseAmount(Math.min(walletBalance, totalAmount));
        } else {
            setWalletUseAmount(0);
        }
    }, [method, totalAmount, walletBalance]);

    const clampedWalletUseAmount = useMemo(() => {
        const numeric = Number(walletUseAmount || 0);
        const min = 0;
        const max = Math.min(walletBalance, totalAmount);
        return Math.min(max, Math.max(min, numeric));
    }, [walletUseAmount, walletBalance, totalAmount]);
    const cardAmount = Number((totalAmount - clampedWalletUseAmount).toFixed(2));

    const validateCart = () => {
        if (!Array.isArray(order.cart) || order.cart.length === 0) {
            toast.error("Your cart is empty. Please return to checkout.");
            return false;
        }
        const invalidItem = order.cart.find((item) => !item?.shopId);
        if (invalidItem) {
            const itemLabel = invalidItem?.name ? `"${invalidItem.name}"` : "One item";
            toast.error(`${itemLabel} is missing seller information. Please remove and re-add it from the product page.`);
            return false;
        }
        return true;
    };

    const handleWalletOnly = async () => {
        if (!validateCart()) return;
        if (walletBalance < totalAmount) {
            toast.error("Insufficient wallet balance for full wallet payment.");
            return;
        }
        setWalletPaying(true);
        try {
            const payload = {
                ...order,
                paymentInfo: {
                    type: "Wallet",
                    status: "Succeeded",
                },
                paidAt: new Date().toISOString(),
                walletAmountUsed: totalAmount,
            };
            const orderResponse = await axios.post(`${server}/order/create-order`, payload, {
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            });
            const firstOrder = orderResponse.data?.orders?.[0];
            localStorage.setItem(
                "successOrderData",
                JSON.stringify({
                    ...orderData,
                    paymentInfo: payload.paymentInfo,
                    orderId: firstOrder?._id || null,
                    orderNumber: firstOrder?.orderNumber ?? null,
                })
            );
            localStorage.setItem("latestOrder", JSON.stringify([]));
            dispatch(clearCart());
            navigate("/order/success");
            dispatch(loadUser());
        } catch (error) {
            toast.error(error?.response?.data?.message || "Wallet payment failed");
        } finally {
            setWalletPaying(false);
        }
    };

    return (
        <div className="w-full flex flex-col items-center py-8">
            <div className="w-[90%] 1000px:w-[70%] block 800px:flex">
                    <div className="w-full 800px:w-[65%]">
                        <PaymentMethodPanel
                            method={method}
                            setMethod={setMethod}
                            walletBalance={walletBalance}
                            totalAmount={totalAmount}
                            walletUseAmount={clampedWalletUseAmount}
                            setWalletUseAmount={setWalletUseAmount}
                            stripeAvailable={stripeAvailable}
                        />
                        {method === "wallet" && cardAmount <= 0 ? (
                            <WalletOnlyPanel onConfirm={handleWalletOnly} paying={walletPaying} />
                        ) : null}
                        {method === "card" || (method === "wallet" && cardAmount > 0) ? (
                            <CardPaymentPanel
                                stripePromise={stripePromise}
                                currency={currency}
                                cardAmount={cardAmount}
                                walletAmountUsed={method === "wallet" ? clampedWalletUseAmount : 0}
                                order={order}
                                orderData={orderData}
                                user={user}
                                walletAutoTopUp={method === "wallet" && cardAmount > 0}
                            />
                        ) : null}
                    </div>
                <div className="w-full 800px:w-[35%] 800px:mt-0 mt-8">
                    <CartData
                        orderData={orderData}
                        paymentMethod={method || "none"}
                        walletAmountUsed={method === "wallet" ? clampedWalletUseAmount : 0}
                        cardAmount={method === "wallet" ? cardAmount : totalAmount}
                    />
                </div>
            </div>
        </div>
    );
};

const PaymentMethodPanel = ({
    method,
    setMethod,
    walletBalance,
    totalAmount,
    stripeAvailable,
}) => (
    <div className="w-full 800px:w-[95%] bg-[#fff] rounded-md p-5 mb-4">
        <h5 className="text-[20px] font-[600] mb-3">Payment method</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
                type="button"
                onClick={() => setMethod("wallet")}
                className={`rounded-lg border p-4 text-left ${
                    method === "wallet" ? "border-[#38513B] bg-[#f5faf5]" : "border-slate-200 bg-white"
                }`}
            >
                <div className="font-[600]">Wallet</div>
                <div className="text-xs text-slate-600 mt-1">
                    {walletBalance >= totalAmount
                        ? `Available: ${formatCurrency(walletBalance)}`
                        : `Available: ${formatCurrency(walletBalance)} - remaining goes to card`}
                </div>
            </button>
            <button
                type="button"
                onClick={() => stripeAvailable && setMethod("card")}
                disabled={!stripeAvailable}
                className={`rounded-lg border p-4 text-left ${
                    method === "card" ? "border-[#38513B] bg-[#f5faf5]" : "border-slate-200 bg-white"
                } ${!stripeAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
            >
                <div className="font-[600]">Credit / Debit Card</div>
                <div className="text-xs text-slate-600 mt-1">
                    {stripeAvailable ? "Pay full amount by card" : "Card unavailable"}
                </div>
            </button>
        </div>
        {!stripeAvailable && (
            <p className="text-xs text-amber-700 mt-2">
                Stripe card payments are currently unavailable. You can still pay fully with wallet.
            </p>
        )}
        {!method && (
            <p className="text-sm text-[#666] mt-3">
                Select a payment option to continue.
            </p>
        )}
    </div>
);

const WalletOnlyPanel = ({ onConfirm, paying = false }) => (
    <div className="w-full 800px:w-[95%] bg-[#fff] rounded-md p-5 pb-8">
        <h5 className="text-[20px] font-[600] mb-5">Wallet Checkout</h5>
        <div className="mb-6 p-4 bg-[#f7faf7] border border-[#d8e7da] rounded-lg">
            <p className="text-[14px] text-[#2f4232]">
                You will be charged immediately from your wallet.
            </p>
        </div>
        <div className="flex justify-end">
            <button
                type="button"
                onClick={onConfirm}
                disabled={paying}
                className={`${styles.button} !bg-[#38513B] text-[#fff] h-[50px] rounded-[5px] cursor-pointer text-[18px] font-[600] px-6 disabled:opacity-60 disabled:cursor-not-allowed`}
            >
                {paying ? "Processing..." : "Pay with Wallet"}
            </button>
        </div>
    </div>
);

const CardPaymentPanel = ({
    stripePromise,
    currency,
    cardAmount,
    walletAmountUsed,
    order,
    orderData,
    user,
    walletAutoTopUp = false,
}) => {
    const [clientSecret, setClientSecret] = useState("");
    const [paymentMethodConfigurationId, setPaymentMethodConfigurationId] = useState("");
    const [loadingIntent, setLoadingIntent] = useState(true);
    const amount = Number(cardAmount || 0);

    const paymentIntentSignature = useMemo(() => {
        const cartSig = (order?.cart || []).map((item) =>
            [
                item?.productId || item?._id || item?.id || "",
                item?.shopId || "",
                item?.qty || 1,
                item?.discountPrice ?? "",
            ].join(":")
        );
        return [
            amount,
            currency || "GBP",
            user?._id || "",
            Number(walletAmountUsed || 0),
            order?.totalPrice || "",
            cartSig.join("|"),
        ].join("~");
    }, [amount, currency, user?._id, walletAmountUsed, order?.totalPrice, order?.cart]);

    useEffect(() => {
        const createIntent = async () => {
            if (!(amount > 0)) {
                setClientSecret("");
                setPaymentMethodConfigurationId("");
                setLoadingIntent(false);
                return;
            }
            setLoadingIntent(true);
            try {
                const buyerName = String(
                    order?.user?.name || user?.name || user?.username || ""
                ).trim();
                const buyerEmail = String(order?.user?.email || user?.email || "").trim();
                const response = await axios.post(
                    `${server}/payment/stripe/intent`,
                    {
                        amount,
                        currency: currency || "GBP",
                        metadata: {
                            flow: "checkout",
                            userId: user?._id || "",
                        },
                        ...(buyerEmail ? { buyerEmail, buyerName: buyerName || undefined } : {}),
                        pendingCheckout: order
                            ? {
                                  orderPayload: order,
                                  orderDataExtras: orderData || null,
                                  walletAmountUsed: Number(walletAmountUsed || 0),
                              }
                            : undefined,
                    },
                    { withCredentials: true }
                );
                setClientSecret(response.data?.clientSecret || "");
                setPaymentMethodConfigurationId(
                    response.data?.paymentMethodConfigurationId || ""
                );
            } catch (error) {
                setClientSecret("");
                setPaymentMethodConfigurationId("");
                toast.error(error?.response?.data?.message || "Unable to initialize card payment");
            } finally {
                setLoadingIntent(false);
            }
        };
        createIntent();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stable signature avoids duplicate PIs
    }, [paymentIntentSignature]);

    if (loadingIntent) {
        return (
            <div className="w-full 800px:w-[95%] bg-[#fff] rounded-md p-5 pb-8">
                <p className="text-gray-600">Preparing payment form...</p>
            </div>
        );
    }
    if (!clientSecret || !stripePromise) {
        return (
            <div className="w-full 800px:w-[95%] bg-[#fff] rounded-md p-5 pb-8">
                <p className="text-red-700 text-sm">
                    Card payment is unavailable right now. Please use wallet or try again later.
                </p>
            </div>
        );
    }

    const stripeBillingDefaults = buildStripeDefaultBilling(orderData, order?.user ?? user);
    const billingPrefillKey = getStripeBillingPrefillKey(stripeBillingDefaults);

    return (
        <Elements
            key={`${clientSecret}__${billingPrefillKey}__${paymentMethodConfigurationId}`}
            stripe={stripePromise}
            options={getStripeElementsOptions({
                clientSecret,
                billingDefaults: stripeBillingDefaults,
                paymentMethodConfiguration: paymentMethodConfigurationId,
            })}
        >
            <StripePaymentForm
                clientSecret={clientSecret}
                order={order}
                orderData={orderData}
                walletAmountUsed={walletAmountUsed}
                walletAutoTopUp={walletAutoTopUp}
                defaultBillingDetails={stripeBillingDefaults}
            />
        </Elements>
    );
};

const StripePaymentForm = ({
    clientSecret = "",
    order,
    orderData,
    walletAmountUsed = 0,
    walletAutoTopUp = false,
    defaultBillingDetails = null,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);

    const runPayment = async () => {
        if (!stripe || !elements) {
            toast.error("Payment system is not ready.");
            return;
        }
        const stockCheck = await assertCartLinesInStock(order.cart);
        if (!stockCheck.ok) {
            toast.error(stockCheck.message || "One or more items are no longer available.");
            return;
        }
        try {
            setIsProcessing(true);
            const { error: submitError } = await elements.submit();
            if (submitError) {
                toast.error(humanizeStripePaymentError(submitError));
                return;
            }
            try {
                saveStripePendingCheckout({
                    order,
                    orderData,
                    walletAmountUsed: Number(walletAmountUsed || 0),
                });
            } catch {
                toast.error(
                    "Your browser could not save checkout for this step. Allow site storage or try another browser."
                );
                return;
            }

            const paymentIntentId = paymentIntentIdFromClientSecret(clientSecret);
            if (paymentIntentId) {
                try {
                    await registerStripePendingCheckout({
                        server,
                        paymentIntentId,
                        order,
                        orderData,
                        walletAmountUsed,
                    });
                } catch (regErr) {
                    clearStripePendingCheckout();
                    toast.error(
                        regErr?.response?.data?.message ||
                            "Could not save your checkout before payment. Please try again."
                    );
                    return;
                }
            }

            const returnUrl = `${window.location.origin}/payment/stripe-return`;
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
                confirmParams: {
                    return_url: returnUrl,
                },
            });

            if (error) {
                clearStripePendingCheckout();
                toast.error(humanizeStripePaymentError(error));
                return;
            }
            // Klarna etc.: Stripe redirects the browser; this handler does not run further until return page.
            if (!paymentIntent) {
                return;
            }
            if (paymentIntent.status !== "succeeded") {
                clearStripePendingCheckout();
                toast.error(
                    `Payment was not completed (status: ${paymentIntent.status}). Please try again or use another method.`
                );
                return;
            }
            clearStripePendingCheckout();
            const payload = {
                ...order,
                paymentInfo: {
                    id: paymentIntent.id,
                    status: paymentIntent.status,
                    type: "Stripe",
                },
                walletAmountUsed: Number(walletAmountUsed || 0),
            };
            const orderResponse = await axios.post(`${server}/order/create-order`, payload, {
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            });
            const firstOrder = orderResponse.data?.orders?.[0];
            localStorage.setItem(
                "successOrderData",
                JSON.stringify({
                    ...orderData,
                    paymentInfo: payload.paymentInfo,
                    orderId: firstOrder?._id || null,
                    orderNumber: firstOrder?.orderNumber ?? null,
                })
            );
            dispatch(clearCart());
            localStorage.setItem("latestOrder", JSON.stringify([]));
            toast.success("Order successful!");
            navigate("/order/success");
        } catch (err) {
            clearStripePendingCheckout();
            const msg =
                err?.response?.data?.message ||
                humanizeStripePaymentError(err) ||
                "Something went wrong with payment. Please try again.";
            toast.error(msg);
        } finally {
            setIsProcessing(false);
        }
    };

    const handlePay = async (e) => {
        e.preventDefault();
        await runPayment();
    };

    return (
        <div className="w-full 800px:w-[95%] bg-[#fff] rounded-md p-5 pb-8">
            <h5 className="text-[20px] font-[600] mb-5">Complete Your Payment</h5>
            {walletAutoTopUp ? (
                <div className="mb-4 p-3 rounded-md border border-[#d8e7da] bg-[#f7faf7] text-sm text-[#2f4232]">
                    Wallet {formatCurrency(walletAmountUsed)} will be used first. Remaining {formatCurrency(Number(order?.totalPrice || 0) - Number(walletAmountUsed || 0))} will be charged to card.
                </div>
            ) : null}
            <form onSubmit={handlePay} className="space-y-6">
                <StripeExpressCheckout onConfirm={runPayment} className="mb-2" />
                <div
                    className={STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS}
                    id="payment-element-container"
                >
                    <PaymentElement options={getStripePaymentElementOptions(defaultBillingDetails)} />
                </div>
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isProcessing}
                        className={`${styles.button} !bg-[#38513B] text-[#fff] h-[50px] rounded-[5px] cursor-pointer text-[18px] font-[600] disabled:opacity-50 disabled:cursor-not-allowed px-6`}
                    >
                        {isProcessing ? "Processing..." : "Pay Now"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const CartData = ({ orderData, paymentMethod = "stripe", walletAmountUsed = 0, cardAmount = 0 }) => {
    const subTotal = orderData?.subTotalPrice || "0.00";
    const discount = orderData?.discountPrice || null;
    const displayTotal = computeBuyerFacingTotalFromCheckoutPayload(orderData);
    const postageFees = orderData?.postageFees || 0;
    const buyerProtectionFee = orderData?.buyerProtectionFee || 0;
    const bundleDiscount = orderData?.bundleDiscount || 0;
    const bundlePostageDiscount = Number(orderData?.bundlePostageDiscount ?? 0);
    const freeShipping = !!orderData?.freeShipping;
    const cart = orderData?.cart || [];
    
    const effectiveMethodLabel =
        String(paymentMethod || "").toLowerCase() === "wallet"
            ? Number(walletAmountUsed || 0) > 0 && Number(cardAmount || 0) > 0
                ? "Wallet + Card"
                : "Wallet"
            : String(paymentMethod || "").toLowerCase() === "card"
            ? "Credit / debit card"
            : "Not selected";

    return (
        <div className="w-full bg-[#fff] rounded-md p-5 pb-8">
            <h3 className="text-[20px] font-[600] mb-4 pb-3 border-b">Order Summary</h3>
            <div className="mb-4 text-[13px] text-[#555]">
                <span className="font-[600]">Payment method: </span>
                {effectiveMethodLabel}
            </div>
            
            {/* Products List */}
            <div className="max-h-[400px] overflow-y-auto mb-4 border-b pb-4">
                {cart.length > 0 ? (
                    <div className="space-y-3">
                        {cart.map((item, index) => (
                            <div key={index} className="flex items-start gap-3 pb-3 border-b last:border-0">
                                <img
                                    src={
                                        item.images && item.images.length > 0
                                            ? item.images[0]?.url
                                                ? `${backend_url}${item.images[0].url}`
                                                : `${backend_url}${item.images[0]}`
                                            : item.image
                                                ? `${backend_url}${item.image}`
                                                : "/placeholder-image.png"
                                    }
                                    alt={item.name || "Product"}
                                    className="w-16 h-16 object-contain rounded-md flex-shrink-0"
                                    onError={(e) => {
                                        e.target.src = "/placeholder-image.png";
                                    }}
                                />
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-[14px] font-[500] text-[#333] line-clamp-2 mb-1">
                                        {normalizeProductDisplayName(item.name)}
                                    </h4>
                                    <p className="text-[12px] text-[#666] mb-1">
                                        Qty: {item.qty}
                                    </p>
                                    <p className="text-[14px] font-[600] text-[#38513B]">
                                        {formatCurrency(item.qty * item.discountPrice)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-[14px] text-[#666] text-center py-4">No items in cart</p>
                )}
            </div>
            
            {/* Price Breakdown */}
            <div className="mt-4 pt-4 border-t space-y-2">
                <div className="flex justify-between text-[14px]">
                    <span className="text-[#666]">Subtotal:</span>
                    <span className="font-[500]">{formatCurrency(Number(subTotal))}</span>
                </div>

                {postageFees > 0 && (
                    <div className="flex justify-between text-[14px]">
                        <span className="text-[#666]">Postage fees:</span>
                        <span className="font-[500]">{formatCurrency(Number(postageFees))}</span>
                    </div>
                )}

                {bundlePostageDiscount > 0 && (
                    <div className="flex justify-between text-[14px]">
                        <span className="text-[#666]">
                            {bundlePostageDiscountLabel(
                                postageFees > 0
                                    ? Math.round((bundlePostageDiscount / Number(postageFees)) * 100)
                                    : 30
                            )}
                        </span>
                        <span className="font-[500] text-green-600">
                            -{formatCurrency(bundlePostageDiscount)}
                        </span>
                    </div>
                )}

                {buyerProtectionFee > 0 && (
                    <div className="flex justify-between text-[14px]">
                        <span className="text-[#666]">Buyer protection fee:</span>
                        <span className="font-[500]">{formatCurrency(Number(buyerProtectionFee))}</span>
                    </div>
                )}

                {bundleDiscount > 0 && (
                    <div className="flex justify-between text-[14px] text-green-600">
                        <span>Bundle discount:</span>
                        <span className="font-[500]">-{formatCurrency(Number(bundleDiscount))}</span>
                    </div>
                )}

                {discount && (
                    <div className="flex justify-between text-[14px] text-green-600">
                        <span>Discount:</span>
                        <span className="font-[500]">-{formatCurrency(Number(discount))}</span>
                    </div>
                )}
                {freeShipping && postageFees > 0 && (
                    <div className="flex justify-between text-[14px] text-green-600">
                        <span>Free shipping:</span>
                        <span className="font-[500]">-{formatCurrency(Number(postageFees))}</span>
                    </div>
                )}

                <div className="flex justify-between text-[18px] font-[600] pt-2 border-t mt-2">
                    <span>Total:</span>
                    <span className="text-[#38513B]">{formatCurrency(displayTotal)}</span>
                </div>
            </div>
        </div>
    );
};

export default Payment;
